import { isString } from 'lodash';

import type { Maybe } from './globals';

/**
 * Comet check-in language tags (values are what the portal persists, e.g. `es-MX`, `pt-BR`).
 * Keys are primary-language codes Neutron uses for greeting / template resolution.
 * Incoming tags are always matched via the primary language subtag (`split('-')[0]`).
 */
export const DRIVER_GREETING_COMET_LOCALES = {
  en: 'en',
  es: 'es-MX',
  fr: 'fr',
  pt: 'pt-BR',
  ar: 'ar',
  pl: 'pl',
  ru: 'ru',
  uk: 'uk',
  hi: 'hi'
} as const;

export type DriverGreetingCometLocale = keyof typeof DRIVER_GREETING_COMET_LOCALES;

/**
 * Canonical (instruction) stop-translate keyword per Comet locale.
 * Typo / alternate forms live only in {@link STOP_TRANSLATE_KEYWORDS} for matching.
 */
export const CANONICAL_STOP_TRANSLATE_KEYWORDS: Record<DriverGreetingCometLocale, string> = {
  en: 'STOP TRANSLATION',
  es: 'DETENER TRADUCCIÓN',
  fr: 'ARRÊTER TRADUCTION',
  pt: 'PARAR TRADUÇÃO',
  ar: 'إيقاف الترجمة',
  pl: 'ZATRZYMAJ TŁUMACZENIE',
  ru: 'СТОП ПЕРЕВОД',
  uk: 'СТОП ПЕРЕКЛАД',
  hi: 'अनुवाद बंद'
};

export const STOP_TRANSLATE_KEYWORDS: readonly string[] = [
  // English
  'STOP TRANSLATION',
  'STOP TRASLATION',
  'STOP TRANSLATE',
  'STOP TRASLATE',

  // Spanish
  'DETENER TRADUCCIÓN',
  'DETENER TRADUCCION',
  'DETENER TRADUCION',
  'DETENER TRADDUCCION',
  'DETENER TRADUCTION',
  'DETENER TRADUC',
  'DEETENER TRADUCCION',
  'DETNER TRADUCCION',
  'PARAR TRADUCCIÓN',
  'PARAR TRADUCCION',
  'PARAR TRADUCION',
  'PARAR TRADUC',
  'STOP TRADUCCIÓN',
  'STOP TRADUCCION',

  // French
  'ARRÊTER TRADUCTION',
  'ARRETER TRADUCTION',
  'ARETER TRADUCTION',
  'ARRÉTER TRADUCTION',
  'ARRETER TRADUCTOIN',
  'ARRETER TRADUCTON',
  'ARRETER TRADUTION',
  'ARRÊTER TRADUCTON',
  'ARRETE TRADUCTION',
  'ARRETEZ TRADUCTION',
  'CESSER TRADUCTION',
  'CESSER LA TRADUCTION',
  'ARRETER TRADUC',
  'STOP TRADUCTION',

  // Brazilian Portuguese
  'PARAR TRADUÇÃO',
  'PARAR TRADUCAO',
  'PARAR TRADUÇAO',
  'PARAR TRADUCÃO',
  'PARAR TRADUCA0',
  'PARAR TRADUZAO',
  'PARA TRADUÇÃO',
  'PARA TRADUCAO',
  'PARAR TRAD',
  'PARAR A TRADUÇÃO',
  'PARAR A TRADUCAO',

  // Arabic + romanized
  'إيقاف الترجمة',
  'ايقاف الترجمة',
  'ايقاف الترجمه',
  'إيقاف الترجمه',
  'ايقافترجمه',
  'وقف الترجمة',
  'وقف الترجمه',
  'ايقاف الترجم',
  'EIQAF ALTARJAMA',
  'EIQAF AL TARJAMA',
  'IQAF ALTARJAMA',
  'IQAF AL TARJAMA',
  'WAQAF ALTARJAMA',
  'STOP TARJAMA',
  'STOP AL TARJAMA',

  // Polish + romanized / typo variants
  'ZATRZYMAJ TŁUMACZENIE',
  'ZATRZYMAJ TLUMACZENIE',
  'ZATRZYMAJ TŁUMACZENI',
  'ZATRZYMAJ TLUMACZENI',
  'ZATRZYMAĆ TŁUMACZENIE',
  'ZATRZYMAC TLUMACZENIE',
  'STOP TŁUMACZENIE',
  'STOP TLUMACZENIE',
  'WYŁĄCZ TŁUMACZENIE',
  'WYLACZ TLUMACZENIE',
  'WYLACZ TŁUMACZENIE',

  // Russian Cyrillic + romanized
  'СТОП ПЕРЕВОД',
  'СТОП ПЕРЕВОДИ',
  'СТОП ПЕРЕВОДТ',
  'СТОП ПЕЕРВОД',
  'СТОП ПЕРЕВДО',
  'СТОП ПЕРЕВОТ',
  'СТОП ПЕРЕОД',
  'СТОП ПЕРЕВ',
  'ВЫКЛ ПЕРЕВОД',
  'ОТКЛ ПЕРЕВОД',
  'ОТКЛЮЧИТЬ ПЕРЕВОД',
  'STOP PEREVOD',
  'STOP PEREOVD',
  'STOP PEREVOT',
  'STOP PERVOD',
  'VYKL PEREVOD',
  'OTKL PEREVOD',

  // Ukrainian Cyrillic + romanized
  'СТОП ПЕРЕКЛАД',
  'СТОП ПЕРЕКЛА',
  'СТОП ПЕРЕКДАД',
  'СТОП ПЕРЕКАЛД',
  'СТОП ПИРЕКЛА',
  'СТОП ПЕРЕКЛ',
  'ЗУПИНИТИ ПЕРЕКЛАД',
  'ЗУПИНИ ПЕРЕКЛАД',
  'ЗУПИН ПЕРЕКЛАД',
  'ВИКЛ ПЕРЕКЛАД',
  'ВІДКЛ ПЕРЕКЛАД',
  'STOP PEREKLAD',
  'STOP PEREKLA',
  'ZUPYNYTY PEREKLAD',
  'ZUPYNY PEREKLAD',
  'VIKL PEREKLAD',

  // Punjabi Gurmukhi + romanized
  'ਅਨੁਵਾਦ ਬੰਦ',
  'ਅਨੁਵਾਦ ਬਨਦ',
  'ਅਨੁਵਾਦਬੰਦ',
  'ANUVAD BAND',
  'ANUWAD BAND',
  'ANUVAAD BAND',
  'ANUAD BAND',
  'ANUVAD BUND',
  'ANUVAD BND',
  'ANUVAD BHAND',
  'ANUVAD BEND',
  'ANUVAD BAAND',
  'ANUVAD BANDH',

  // Hindi Devanagari + romanized
  'अनुवाद बंद',
  'अनुवाद रोकें',
  'अनुवाद रोको',
  'अनुवाद रोके',
  'अनुवादरोकें',
  'ANUVAD ROKEIN',
  'ANUVAD ROKEN',
  'ANUVAD ROKAIN',
  'ANUVAD ROKE',
  'ANUWAD ROKEIN',
  'ANUVAAD ROKEIN',
  'ANUVAD ROKHEIN',
  'ANUVAD ROKIEN',
  'ANUVAD ROKNE',

  // Tagalog
  'ITIGIL PAGSASALIN',
  'ITIGIL PAGSALIN',
  'ITIGIL PAGSASLIN',
  'ITIGIL PAGASALIN',
  'ITIGIL PAGSASALN',
  'ITIGIL PAGSASALING',
  'TIGIL PAGSASALIN',
  'TIGIL PAGSALIN',
  'ITIGIL SALIN',
  'ITIGIL ANG PAGSASALIN',
  'ITIGIL ANG SALIN',
  'ITTIGIL PAGSASALIN',
  'ITGIL PAGSASALIN',

  // Haitian Creole
  'KANPE TRADIKSYON',
  'KANPE TRADIKSON',
  'KANPE TRADIKSION',
  'KANPE TRADIKYON',
  'KANPE TRADUKSYON',
  'KANPE TRADISYON',
  'KANP TRADIKSYON',
  'KANPE TRADIKSYO',
  'KANPE TRADUCTION',
  'KANPE TRADIK',
  'RETE TRADIKSYON',
  'RETE TRADIKSON'
];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
}

const STOP_TRANSLATE_PATTERN = new RegExp(
  `^(?:${STOP_TRANSLATE_KEYWORDS.map(escapeRegExp).join('|')})$`,
  'iu'
);

export function isStopTranslate(content: Maybe<string>): boolean {
  if (!isString(content)) {
    return false;
  }
  return STOP_TRANSLATE_PATTERN.test(content.trim());
}

export function shouldSkipTranslation(
  input:
    | { direction: 'inbound'; translationEnabled: boolean }
    | { direction: 'outbound'; driverTranslationEnabled: boolean }
): boolean {
  if (input.direction === 'inbound') {
    return input.translationEnabled === false;
  }
  return input.driverTranslationEnabled === false;
}

export function canonicalStopTranslateKeyword(locale: DriverGreetingCometLocale): string {
  return CANONICAL_STOP_TRANSLATE_KEYWORDS[locale];
}
